import React, { useState, useReducer, useRef, useEffect, useCallback } from "react";
import { COLORS } from "../../theme/colors";
import { Button } from "../../components/ui";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import { agentApi, mcpApi } from "../../api/client";
import { configReducer, INITIAL_CONFIG, buildPayload } from "./engine/configAccumulator";
import {
  STEPS, buildStepMessage, buildUserMessage, buildErrorMessage, buildInfoMessage,
  getDisplayLabel, resolveDefault, getNextStepIndex,
} from "./engine/stateMachine";

/* ── useAgentChat hook ─────────────────────────────────────
 * Core hook that drives the conversation. In Option A it uses a local
 * state machine. In Option B (future), swap the engine internals to
 * call an LLM backend — the hook signature stays identical.
 */
function useAgentChat({ availableModels, mcpServers, availableAgents, onCreated }) {
  const [messages, setMessages] = useState([]);
  const [config, dispatch] = useReducer(configReducer, INITIAL_CONFIG);
  const [stepIndex, setStepIndex] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [multiSelectBuffer, setMultiSelectBuffer] = useState(null);
  const context = { availableModels, mcpServers, availableAgents };

  // Push the first message on mount
  useEffect(() => {
    const firstStep = STEPS[0];
    setMessages([buildStepMessage(firstStep, INITIAL_CONFIG, context)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentStep = STEPS[stepIndex] || null;

  const advanceToStep = useCallback((nextIdx, currentConfig) => {
    setStepIndex(nextIdx);
    const step = STEPS[nextIdx];
    setTimeout(() => {
      setMessages((prev) => [...prev, buildStepMessage(step, currentConfig, context)]);
    }, 400);
  }, [context]);

  const processAnswer = useCallback((value, displayText) => {
    if (isComplete || isCreating) return;
    const step = STEPS[stepIndex];
    if (!step) return;

    // User message
    setMessages((prev) => [...prev, buildUserMessage(displayText || String(value))]);

    // Transform
    const transformed = step.transform ? step.transform(value) : value;

    // Validate
    const error = step.validate ? step.validate(transformed, config) : null;
    if (error) {
      setTimeout(() => setMessages((prev) => [...prev, buildErrorMessage(error)]), 300);
      return;
    }

    // Update config
    if (step.fieldPath) {
      dispatch({ type: "SET_FIELD", field: step.fieldPath, value: transformed });
    }

    // Build updated config for next step's skipIf and dynamic options
    const updatedConfig = { ...config, ...(step.fieldPath ? { [step.fieldPath]: transformed } : {}) };

    // Advance, respecting skipIf
    const nextIdx = getNextStepIndex(stepIndex, updatedConfig);
    if (nextIdx === stepIndex && step.type === "confirm") {
      setIsComplete(true);
    } else {
      advanceToStep(nextIdx, updatedConfig);
    }
  }, [stepIndex, config, isComplete, isCreating, advanceToStep]);

  const sendMessage = useCallback((text) => {
    if (!text.trim()) return;
    const step = STEPS[stepIndex];
    if (!step || step.type === "confirm") return;
    processAnswer(text.trim(), text.trim());
  }, [stepIndex, processAnswer]);

  const selectQuickReply = useCallback((value) => {
    const step = STEPS[stepIndex];
    if (!step) return;

    if (value === "__create__") {
      confirmAndCreate();
      return;
    }
    if (value === "__reset__") {
      reset();
      return;
    }
    if (value === "__none__") return; // Placeholder, do nothing

    if (step.type === "multi-select") {
      setMultiSelectBuffer((prev) => {
        const current = prev || [];
        if (current.includes(value)) return current.filter((v) => v !== value);
        return [...current, value];
      });
      return;
    }

    // Single select
    const display = getDisplayLabel(step, value, config, context);
    processAnswer(value, display);
  }, [stepIndex, config, processAnswer, context]);

  const confirmMultiSelect = useCallback(() => {
    const step = STEPS[stepIndex];
    if (!step || step.type !== "multi-select") return;
    const values = multiSelectBuffer || [];
    const display = getDisplayLabel(step, values, config, context);
    setMultiSelectBuffer(null);
    processAnswer(values, display || "None selected");
  }, [stepIndex, multiSelectBuffer, config, processAnswer, context]);

  const confirmAndCreate = useCallback(async () => {
    setIsCreating(true);
    setCreateResult(null);
    try {
      const payload = buildPayload(config, { availableAgents });
      const res = await agentApi.create(payload);
      if (res.success) {
        setCreateResult({ success: true, data: res.data });
        setMessages((prev) => [...prev, {
          id: `msg-${Date.now()}`, role: "assistant",
          content: `Agent "${config.name}" created successfully! You can now open it in the visual builder to fine-tune the configuration.`,
          quickReplies: [{ label: "Create Another", value: "__reset__", icon: "+" }],
          meta: { isSuccess: true },
        }]);
        setIsComplete(true);
        onCreated?.(res.data);
      } else {
        throw new Error(res.message || "Creation failed");
      }
    } catch (err) {
      setCreateResult({ success: false, error: err.message });
      setMessages((prev) => [...prev, buildErrorMessage(`Failed to create agent: ${err.message}`)]);
    } finally {
      setIsCreating(false);
    }
  }, [config, availableAgents, onCreated]);

  const reset = useCallback(() => {
    dispatch({ type: "RESET" });
    setStepIndex(0);
    setIsComplete(false);
    setIsCreating(false);
    setCreateResult(null);
    setMultiSelectBuffer(null);
    const firstStep = STEPS[0];
    setMessages([buildStepMessage(firstStep, INITIAL_CONFIG, context)]);
  }, [context]);

  return {
    messages, config, currentStep, stepIndex,
    isComplete, isCreating, createResult,
    multiSelectBuffer,
    sendMessage, selectQuickReply, confirmMultiSelect,
    confirmAndCreate, reset,
  };
}


/* ── AgentChatView ─────────────────────────────────────────── */
export default function AgentChatView({ availableModels, onCreated, onSwitchToCanvas }) {
  const bp = useBreakpoint();
  const scrollRef = useRef(null);
  const [inputText, setInputText] = useState("");
  const [showPreview, setShowPreview] = useState(false);
  const [mcpServers, setMcpServers] = useState([]);
  const [availableAgents, setAvailableAgents] = useState([]);

  // Fetch MCP servers and existing agents on mount
  useEffect(() => {
    mcpApi.listServers().then((res) => setMcpServers(res.data?.servers || [])).catch(() => {});
    agentApi.list().then((res) => setAvailableAgents((res.data || []).filter((a) => !a.workflow_id))).catch(() => {});
  }, []);

  const {
    messages, config, currentStep, stepIndex,
    isComplete, isCreating,
    multiSelectBuffer,
    sendMessage, selectQuickReply, confirmMultiSelect,
    reset,
  } = useAgentChat({ availableModels, mcpServers, availableAgents, onCreated });

  // Pinned-to-bottom auto-scroll (preserves user scroll-up during streaming).
  //
  // The earlier behaviour set scrollTop = scrollHeight on every messages
  // change, which during token-by-token streaming meant the scroll yanked
  // to bottom 20-30 times per second — defeating any attempt by the user
  // to scroll back to read earlier text. Standard chat pattern: only
  // auto-scroll if the user is already pinned to (or near) the bottom.
  // The "near" threshold (40px) lets short reflows still snap; once the
  // user scrolls up beyond it, no further auto-scroll happens until they
  // manually return to the bottom.
  const pinnedToBottom = useRef(true);
  const handleScrollableScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    pinnedToBottom.current = distanceFromBottom < 40;
  }, []);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScrollableScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScrollableScroll);
  }, [handleScrollableScroll]);
  useEffect(() => {
    if (pinnedToBottom.current && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    if (!inputText.trim()) return;
    sendMessage(inputText);
    setInputText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const isTextStep = currentStep && (currentStep.type === "text" || currentStep.type === "textarea");
  const isMultiSelectActive = currentStep?.type === "multi-select" && !isComplete;
  const totalSteps = STEPS.filter((s, i) => !s.skipIf || !s.skipIf(config)).length;

  return (
    <div style={{ flex: 1, display: "flex", overflow: "hidden", background: COLORS.surface }}>
      <div style={{
        flex: 1, display: "flex", flexDirection: "column",
        maxWidth: bp.isMobile ? "100%" : 720,
        margin: bp.isMobile ? 0 : "0 auto",
        borderLeft: bp.isMobile ? "none" : `1px solid ${COLORS.border}`,
        borderRight: bp.isMobile ? "none" : `1px solid ${COLORS.border}`,
      }}>
        {/* Header */}
        <div style={{
          padding: "12px 20px", borderBottom: `1px solid ${COLORS.border}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18 }}>{"\uD83E\uDD16"}</span>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.text }}>Agent Builder</div>
              <div style={{ fontSize: 10, color: COLORS.textMuted }}>
                Step {Math.min(stepIndex + 1, STEPS.length)} of {STEPS.length}
                {mcpServers.length > 0 && ` \u00B7 ${mcpServers.length} tool servers`}
                {availableAgents.length > 0 && ` \u00B7 ${availableAgents.length} agents`}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {config.name && (
              <button onClick={() => setShowPreview((p) => !p)} style={{
                padding: "5px 10px", borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: "pointer",
                background: showPreview ? COLORS.accentSoft : "transparent",
                border: `1px solid ${COLORS.border}`, color: COLORS.textMuted,
              }}>{showPreview ? "Hide Preview" : "Preview"}</button>
            )}
            {onSwitchToCanvas && (
              <button onClick={onSwitchToCanvas} style={{
                padding: "5px 10px", borderRadius: 6, fontSize: 10, fontWeight: 600, cursor: "pointer",
                background: "transparent", border: `1px solid ${COLORS.border}`, color: COLORS.textMuted,
              }}>Canvas</button>
            )}
          </div>
        </div>

        {/* Progress bar */}
        <div style={{ height: 3, background: COLORS.border }}>
          <div style={{
            height: "100%", background: COLORS.accent,
            width: `${((stepIndex + 1) / STEPS.length) * 100}%`,
            transition: "width 0.4s ease",
          }} />
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="panel-scroll" style={{
          flex: 1, overflowY: "auto", padding: "20px 20px 12px",
          display: "flex", flexDirection: "column", gap: 12,
        }}>
          {messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)}
          {isCreating && (
            <div style={{ display: "flex", gap: 8, alignItems: "center", padding: "8px 0" }}>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: COLORS.accent, animation: "fadeIn 0.5s infinite alternate" }} />
              <span style={{ fontSize: 12, color: COLORS.textMuted }}>Creating agent...</span>
            </div>
          )}
        </div>

        {/* Config preview (inline) */}
        {showPreview && config.name && (
          <div style={{
            padding: "12px 20px", borderTop: `1px solid ${COLORS.border}`,
            background: COLORS.surface, maxHeight: 200, overflowY: "auto",
          }}>
            <ConfigSummary config={config} availableAgents={availableAgents} compact />
          </div>
        )}

        {/* Quick replies for last assistant message */}
        {!isComplete && messages.length > 0 && (() => {
          const last = messages[messages.length - 1];
          if (last.role !== "assistant" || !last.quickReplies) return null;
          if (last.isConfirm) return null; // Handled separately below
          return (
            <div style={{
              padding: "8px 20px 4px", display: "flex", flexWrap: "wrap", gap: 6,
              borderTop: `1px solid ${COLORS.border}`,
            }}>
              {last.quickReplies.map((qr) => {
                const isSelected = isMultiSelectActive && (multiSelectBuffer || []).includes(qr.value);
                return (
                  <button key={String(qr.value)} onClick={() => selectQuickReply(qr.value)} style={{
                    padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                    cursor: "pointer", transition: "all 0.15s",
                    background: isSelected ? COLORS.accentSoft : COLORS.surface,
                    border: `1px solid ${isSelected ? COLORS.accent : COLORS.border}`,
                    color: isSelected ? COLORS.accent : COLORS.text,
                  }}>
                    {qr.icon && <span style={{ marginRight: 4 }}>{qr.icon}</span>}
                    {qr.label}
                  </button>
                );
              })}
              {isMultiSelectActive && (
                <button onClick={confirmMultiSelect} style={{
                  padding: "6px 14px", borderRadius: 20, fontSize: 12, fontWeight: 700,
                  cursor: "pointer", background: COLORS.accent, border: "none", color: "#fff",
                }}>Confirm{currentStep?.allowEmpty ? "" : ` (${(multiSelectBuffer || []).length})`}</button>
              )}
            </div>
          );
        })()}

        {/* Confirm step */}
        {!isComplete && currentStep?.type === "confirm" && messages.length > 0 && (() => {
          const last = messages[messages.length - 1];
          if (!last.isConfirm) return null;
          return (
            <div style={{ padding: "0 20px 8px" }}>
              <ConfigSummary config={config} availableAgents={availableAgents} />
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                {last.quickReplies?.map((qr) => (
                  <Button key={String(qr.value)} variant={qr.value === "__create__" ? "primary" : "secondary"} size="sm"
                    onClick={() => selectQuickReply(qr.value)} disabled={isCreating}>
                    {qr.icon && <span>{qr.icon}</span>} {qr.label}
                  </Button>
                ))}
              </div>
            </div>
          );
        })()}

        {/* Input bar */}
        <div style={{
          padding: "12px 20px", borderTop: `1px solid ${COLORS.border}`,
          display: "flex", gap: 8, alignItems: "flex-end",
        }}>
          {currentStep?.type === "textarea" ? (
            <textarea
              value={inputText} onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={currentStep?.placeholder || "Type your answer..."}
              rows={3}
              disabled={!isTextStep || isCreating || isComplete}
              style={{
                flex: 1, padding: "10px 14px", borderRadius: 12, fontSize: 13,
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                color: COLORS.text, outline: "none", resize: "none", fontFamily: "inherit",
              }}
            />
          ) : (
            <input
              type="text" value={inputText} onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={isTextStep ? (currentStep?.placeholder || "Type your answer...") : "Select an option above"}
              disabled={!isTextStep || isCreating || isComplete}
              style={{
                flex: 1, padding: "10px 14px", borderRadius: 12, fontSize: 13,
                background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                color: COLORS.text, outline: "none", fontFamily: "inherit",
              }}
            />
          )}
          <button onClick={handleSend}
            disabled={!inputText.trim() || !isTextStep || isCreating || isComplete}
            style={{
              width: 40, height: 40, borderRadius: 12, border: "none",
              background: inputText.trim() && isTextStep ? COLORS.accent : COLORS.border,
              color: "#fff", fontSize: 16, cursor: inputText.trim() && isTextStep ? "pointer" : "default",
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>{"\u2191"}</button>
        </div>
      </div>
    </div>
  );
}


/* ── MessageBubble ────────────────────────────────────────── */
function MessageBubble({ msg }) {
  const [hovered, setHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const isUser = msg.role === "user";
  const isError = msg.meta?.isError;
  const isSuccess = msg.meta?.isSuccess;

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content || "").then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  return (
    <div
      style={{ display: "flex", justifyContent: isUser ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 6 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Copy button left-of-bubble for user messages */}
      {isUser && (
        <button
          onClick={handleCopy}
          title="Copy message"
          style={{
            visibility: hovered ? "visible" : "hidden",
            background: "transparent", border: "none",
            color: COLORS.textDim, cursor: "pointer",
            padding: "2px 4px", borderRadius: 4, fontSize: 13,
            transition: "color 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = COLORS.text}
          onMouseLeave={e => e.currentTarget.style.color = COLORS.textDim}
        >{copied ? "✓" : "⧉"}</button>
      )}

      <div style={{
        maxWidth: "85%", padding: "10px 16px",
        borderRadius: isUser ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
        background: isError ? `${COLORS.pink}15` : isSuccess ? `${COLORS.green}15`
          : isUser ? COLORS.accent : COLORS.surface,
        color: isError ? COLORS.pink : isSuccess ? COLORS.green
          : isUser ? "#fff" : COLORS.text,
        border: isUser ? "none" : `1px solid ${COLORS.border}`,
        fontSize: 13, lineHeight: 1.5, whiteSpace: "pre-wrap",
      }}>{msg.content}</div>

      {/* Copy button right-of-bubble for assistant messages */}
      {!isUser && (
        <button
          onClick={handleCopy}
          title="Copy message"
          style={{
            visibility: hovered ? "visible" : "hidden",
            background: "transparent", border: "none",
            color: COLORS.textDim, cursor: "pointer",
            padding: "2px 4px", borderRadius: 4, fontSize: 13,
            transition: "color 0.15s",
          }}
          onMouseEnter={e => e.currentTarget.style.color = COLORS.text}
          onMouseLeave={e => e.currentTarget.style.color = COLORS.textDim}
        >{copied ? "✓" : "⧉"}</button>
      )}
    </div>
  );
}


/* ── ConfigSummary ────────────────────────────────────────── */
function ConfigSummary({ config, availableAgents, compact }) {
  const sections = [
    { heading: "Basics", items: [
      { label: "Name", value: config.name },
      ...(!compact ? [{ label: "System Prompt", value: config.system_prompt?.substring(0, 150) + (config.system_prompt?.length > 150 ? "..." : "") }] : []),
      { label: "Channels", value: (config.channels || []).join(", ") || "none" },
      { label: "Default Agent", value: config.is_default ? "Yes" : "No" },
    ]},
    { heading: "LLM", items: [
      { label: "Provider", value: config.provider },
      { label: "Model", value: config.model },
      { label: "Temperature", value: config.temperature },
      { label: "Max Tokens", value: config.max_tokens },
    ]},
    { heading: "Safety", items: [
      { label: "Guardrails", value: (config._guardrails || []).join(", ") || "none" },
      { label: "Escalation", value: config._escalation_enabled ? "Enabled" : "Disabled" },
    ]},
    { heading: "Features", items: [
      { label: "Knowledge (RAG)", value: config._knowledge_enabled ? "Enabled" : "Disabled" },
      { label: "MCP Tools", value: config._tools_enabled
        ? ((config._allowed_servers || []).length > 0 ? `${config._allowed_servers.length} server(s)` : "All servers")
        : "Disabled" },
      { label: "Routing Intents", value: (config.routing_intents || []).length > 0 ? config.routing_intents.join(", ") : "None" },
    ]},
  ];

  if (config._orchestration_enabled && config._orchestration_agent_ids?.length > 0) {
    const agentNames = config._orchestration_agent_ids.map((id) => {
      const a = (availableAgents || []).find((x) => x.id === id);
      return a ? a.name : id.substring(0, 8);
    });
    sections.push({
      heading: "Orchestration",
      items: [
        { label: "Pattern", value: config._orchestration_pattern },
        { label: "Sub-Agents", value: agentNames.join(" \u2192 ") },
      ],
    });
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: compact ? 8 : 14 }}>
      {sections.map((sec) => (
        <div key={sec.heading}>
          {!compact && (
            <div style={{ fontSize: 10, fontWeight: 700, color: COLORS.accentText, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
              {sec.heading}
            </div>
          )}
          <div style={{ display: "grid", gridTemplateColumns: compact ? "1fr 1fr" : "1fr", gap: compact ? "4px 16px" : 6 }}>
            {sec.items.map((item) => (
              <div key={item.label} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.textDim, textTransform: "uppercase", minWidth: compact ? 60 : 90, flexShrink: 0 }}>
                  {item.label}
                </span>
                <span style={{ fontSize: 12, color: COLORS.text, wordBreak: "break-word" }}>
                  {item.value || "\u2014"}
                </span>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
